import { Outlet, Navigate, useLocation } from "react-router-dom";
import { LogOut } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import { Toaster } from "sonner";
import AssistantWidget from "@/components/AssistantWidget";
import { useAuth } from "@/context/AuthContext";

// Opt-in section RBAC: a restricted role may only visit paths whose section it was
// granted. Prefix-matched against the pathname; the first entry a user canSee is also
// where a disallowed navigation (or the post-login "/") lands.
const ROUTE_SECTIONS = [
  { prefix: "/dashboard", perm: "dashboard.view" },
  { prefix: "/ballooning", perm: "ballooning.view" },
  { prefix: "/kc-test", perm: "ballooning.view" },
  { prefix: "/drawing-extract", perm: "ballooning.view" },
  { prefix: "/rfqs", perm: "rfq.view" },
  { prefix: "/npd", perm: "npd.view" },
  { prefix: "/assemblies", perm: "assembly.view" },
  { prefix: "/cost-breakdown", perm: "view_cost_breakdown" },
  { prefix: "/apqp", perm: "apqp.view" },
  { prefix: "/reports", perm: "view_reports" },
  { prefix: "/customers", perm: "manage_customers" },
  { prefix: "/materials", perm: "material.view" },
  { prefix: "/operations", perm: "operation.view" },
  { prefix: "/machines", perm: "edit_settings" },
];

const ROLE_LABEL = { admin: "Administrator", npd_manager: "NPD Manager", npd_engineer: "NPD Engineer" };
const ROLE_COLOR = {
  admin: "bg-orange-500/15 text-orange-700 border-orange-500/30",
  npd_manager: "bg-sky-500/15 text-sky-700 border-sky-500/30",
  npd_engineer: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
};

function TopUserBar() {
  const { user, logout } = useAuth();
  if (!user) return null;
  return (
    <header className="sticky top-0 z-30 h-14 bg-white border-b border-slate-200 flex items-center justify-end gap-3 px-4" data-testid="top-user-bar">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white font-bold text-sm shrink-0">
        {user.name?.[0]?.toUpperCase() || "U"}
      </div>
      <div className="min-w-0 hidden sm:block leading-tight text-right">
        <div className="text-sm font-medium text-slate-800 truncate">{user.name}</div>
        <div className="text-[11px] text-slate-400 truncate">{user.email}</div>
      </div>
      <span
        className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded border ${ROLE_COLOR[user.role] || "bg-slate-500/15 text-slate-600 border-slate-500/30"}`}
        data-testid="top-role-badge"
      >
        {ROLE_LABEL[user.role] || user.role}
      </span>
      <button
        type="button"
        onClick={logout}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-white hover:bg-slate-700 border border-slate-200 hover:border-slate-700 rounded-md px-2.5 py-1.5 transition-colors"
        data-testid="logout-button"
        title="Sign out"
      >
        <LogOut size={14} /> <span className="hidden sm:inline">Sign out</span>
      </button>
    </header>
  );
}

export default function Layout() {
  const { restricted, canSee } = useAuth();
  const location = useLocation();
  // A restricted role is kept inside its granted sections. Any other path (incl. the
  // post-login "/" or a typed-in /npd URL) bounces to the first section it can see.
  if (restricted) {
    const p = location.pathname;
    const match = ROUTE_SECTIONS.find((s) => p === s.prefix || p.startsWith(s.prefix + "/"));
    const allowed = match ? canSee(match.perm) : false;
    if (!allowed) {
      const first = ROUTE_SECTIONS.find((s) => canSee(s.perm));
      return <Navigate to={first ? first.prefix : "/ballooning"} replace />;
    }
  }
  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 min-w-0 flex flex-col overflow-x-hidden">
        <TopUserBar />
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
      <Toaster position="top-right" richColors />
      <AssistantWidget />
    </div>
  );
}
